import { Circle } from '@mui/icons-material';
import _ from 'lodash';
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ContainerEventChange,
  ContainerHistoryEvent,
  fetchContainerHistory,
} from 'services/clients/watchList';
import { Container } from 'types/Watchlist';
import { Loader } from 'components/Loaders/loader';
import { getMapping, safetyCheck } from './components/transforms';
import { HistoryCard } from './HistoryCard';
import { EventFilter, Filter } from './components/Filter';

interface Props {
  container: Container;
}

export const History: React.FC<Props> = (props) => {
  const { container } = props;

  const [filter, setFilter] = React.useState<EventFilter>(EventFilter.AllEvents);

  const { isLoading, isError, data } = useQuery({
    queryKey: ['containerHistory', container.transaction],
    queryFn: () => fetchContainerHistory(container.transaction),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
    refetchOnWindowFocus: false,
  });

  // Formats the event change to be displayed in the table with their respective labels,
  // and transforms/formats the column values
  const eventData = React.useMemo(() => {
    if (!data) return [];

    const items = data
      ?.filter((event: ContainerHistoryEvent) => {
        if (filter === EventFilter.MilestonesOnly) return event.eventType === 'milestone';
        if (filter === EventFilter.InfoChangeOnly)
          return (
            event.eventType === 'return_update' || event.eventType === 'container_availability'
          );
        return true;
      })
      ?.map((event: ContainerHistoryEvent) => {
        const changeMapping = getMapping(event);
        const transformedChanges = event.changes?.map((change: ContainerEventChange) => {
          const { label, transform } = changeMapping[_.camelCase(change.fieldKey)];
          const safeTransform = safetyCheck(transform);
          return {
            ...change,
            label,
            leftCol: safeTransform(change.oldValue, event),
            rightCol: safeTransform(change.newValue, event),
          };
        });
        return {
          ...event,
          changes: transformedChanges,
        };
      });
    return items;
  }, [data, filter]);

  return (
    <div className="p-7">
      <div className="text-right mb-5">
        <Filter filter={filter} setFilter={setFilter} />
      </div>
      {isLoading && (
        <div className="flex justify-center items-center h-40">
          <Loader />
        </div>
      )}
      {!isLoading && eventData.length === 0 && (
        <div className="flex justify-center items-center h-40">
          <h4 className="font-medium text-gray-700">No events to show</h4>
        </div>
      )}
      {isError && (
        <div className="flex justify-center items-center h-40">
          <h4 className="font-medium text-red-900">Error fetching history</h4>
        </div>
      )}
      {!isLoading &&
        !isError &&
        eventData.length > 0 &&
        eventData.map((event: ContainerHistoryEvent, index: number) => {
          return (
            <div key={event.occurredAt} className="flex flex-col gap-5 mb-5">
              <HistoryCard event={event} />
              {index !== eventData.length - 1 && (
                <div className="flex flex-col gap-px ml-9">
                  <Circle className="w-1 text-gray-400 h-2" />
                  <Circle className="w-1 text-gray-400 h-2" />
                  <Circle className="w-1 text-gray-400 h-2" />
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

History.displayName = 'Shipments.DetailDrawer.Sections.History';
